import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaBriefcase,
} from "react-icons/fa";
import apiRequest from "../services/api";

const JobPositionManagement = () => {
  const [positions, setPositions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Modal Sheet View Controllers
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [selectedPosition, setSelectedPosition] = useState(null);

  // Form State mirror-mapped to JobPosition
  const [formData, setFormData] = useState({
    id: null,
    name: "",
  });

  // Fetch Job Positions
  const fetchPositions = async () => {
    setIsLoading(true);
    setError("");

    try {
      const resp = await apiRequest("employees/positions");
      setPositions(resp.data || []);
    } catch (err) {
      setError(err?.message || "Failed to fetch job positions.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPositions();
  }, []);

  // Open Modal
  const openModal = (mode, position = null) => {
    setModalMode(mode);
    setSelectedPosition(position);
    setError("");

    if (position) {
      setFormData({
        id: position.id,
        name: position.name || "",
      });
    } else {
      setFormData({
        id: null,
        name: "",
      });
    }

    setIsModalOpen(true);
  };

  // Save / Update
  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name || formData.name.trim() === "") {
      setError("Job Position Name is required.");
      return;
    }

    if (
      modalMode === "edit" &&
      selectedPosition &&
      formData.name.trim().toLowerCase() === selectedPosition.name.toLowerCase()
    ) {
      setError("Name already exists.");
      return;
    }

    const endpoint = "employees/positions";
    const method = modalMode === "create" ? "POST" : "PUT";

    try {
      await apiRequest(endpoint, {
        method,
        body: {
          ...formData,
          name: formData.name.trim(),
        },
      });

      fetchPositions();
      setIsModalOpen(false);
    } catch (err) {
      setError(err?.message || "Job position rejected.");
    }
  };

  // Delete
  const handleDelete = async (id, name) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the job position "${name}"?`
      )
    ) {
      return;
    }

    try {
      await apiRequest(`employees/positions/${id}`, {
        method: "DELETE",
      });

      fetchPositions();
    } catch (err) {
      setError(err?.message || "Unable to delete record.");
    }
  };

  const inputClass =
    "w-full bg-slate-50 border border-slate-200 text-slate-800 placeholder-slate-400 rounded-xl px-4 py-3 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50 disabled:opacity-60 transition";

  const labelClass =
    "text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1";

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100 relative pb-24">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl p-6 rounded-2xl backdrop-blur-md">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
            <FaBriefcase className="text-emerald-500" />
            Job Positions
          </h1>

          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Manage job titles.
          </p>
        </div>

        <button
          onClick={() => openModal("create")}
          className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-emerald-600/20"
        >
          <FaPlus />
          New
        </button>
      </div>

      {/* Error Banner */}
      {error && (
        <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Positions Table */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] font-bold tracking-wider uppercase dark:border-white/10 dark:bg-slate-950/50 dark:text-slate-400">
                <th className="px-6 py-4">Job Title</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-slate-700 text-xs dark:divide-white/5 dark:text-slate-300">
              {isLoading ? (
                <tr>
                  <td
                    colSpan="2"
                    className="text-center py-10 text-slate-400 dark:text-slate-500"
                  >
                    Querying...
                  </td>
                </tr>
              ) : positions.length === 0 ? (
                <tr>
                  <td
                    colSpan="2"
                    className="text-center py-10 text-slate-400 dark:text-slate-500"
                  >
                    No job positions registered.
                  </td>
                </tr>
              ) : (
                positions.map((pos) => (
                  <tr
                    key={pos.id}
                    className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
                  >
                    <td className="px-6 py-4 font-semibold">
                      <span className="inline-flex px-3 py-1 rounded-md bg-slate-100 border border-slate-200 text-slate-700 tracking-wide text-xs dark:bg-slate-950/80 dark:border-white/10 dark:text-slate-200">
                        {pos.name}
                      </span>
                    </td>

                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end gap-2">
                        {/* View */}
                        <button
                          onClick={() => openModal("view", pos)}
                          className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 border border-slate-200 transition dark:bg-slate-800/80 dark:hover:bg-slate-700 dark:text-slate-300 dark:border-white/5"
                          title="View"
                        >
                          <FaEye />
                        </button>

                        {/* Edit */}
                        <button
                          onClick={() => openModal("edit", pos)}
                          className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-600 border border-blue-100 transition dark:bg-blue-600/10 dark:hover:bg-blue-600/20 dark:text-blue-400 dark:border-blue-500/10"
                          title="Modify"
                        >
                          <FaEdit />
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(pos.id, pos.name)}
                          className="p-2 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 border border-red-100 transition dark:bg-red-600/10 dark:hover:bg-red-600/20 dark:text-red-400 dark:border-red-500/10"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bottom Sheet Modal */}
      <div
        className={`fixed inset-0 z-[2000] flex flex-col justify-end transition-opacity duration-300 ${
          isModalOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Overlay */}
        <div
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-slate-950/70"
          onClick={() => setIsModalOpen(false)}
        />

        {/* Sheet */}
        <div
          className={`relative w-full max-w-xl mx-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-t-3xl p-6 sm:p-8 shadow-[0_-15px_40px_rgba(0,0,0,0.18)] dark:shadow-[0_-15px_40px_rgba(0,0,0,0.5)] transition-transform duration-300 transform space-y-6 ${
            isModalOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          {/* Modal Header */}
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-lg font-bold text-slate-900 dark:text-white capitalize">
                {modalMode} Position
              </h3>

              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {modalMode === "view" ? "" : "Enter descriptive details below."}
              </p>
            </div>

            <button
              onClick={() => setIsModalOpen(false)}
              className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 transition-colors dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
            >
              <FaTimes className="text-sm" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSave} className="space-y-6">
            <div className="flex flex-col gap-1.5">
              <label className={labelClass}>
                <FaBriefcase className="text-[10px] text-slate-400 dark:text-slate-500" />
                Job Position Name
              </label>

              <input
                type="text"
                required
                placeholder="e.g. Salesperson"
                disabled={modalMode === "view"}
                value={formData.name}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    name: e.target.value,
                  })
                }
                className={inputClass}
              />
            </div>

            {/* Modal Actions Footer */}
            <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/5">
              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 text-xs font-semibold border border-slate-200 transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
              >
                Close
              </button>

              {modalMode !== "view" && (
                <button
                  type="submit"
                  className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold shadow-md shadow-emerald-600/10 transition"
                >
                  {modalMode === "create" ? "Save" : "Apply Change"}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default JobPositionManagement;
